(ns ui.view.opensearch-cards.view
  (:require
   [clojure.string :as string]
   [common.localization :as loc]
   [common.opensearch.days-since :as days-since]
   [frontend.state.current-session :as session]
   [frontend.utils :as fu]
   [ui.components.card.kanban :as card-kanban]
   [ui.components.highlight-text.view :as highlight-text]
   [ui.dom-globals :as dg]
   [ui.util :as ui-util]
   [ui.view.opensearch-value.currency :as currency]
   [ui.view.opensearch-value.format :as value-format]))

(defn- path-segment-candidates
  [segment]
  (cond
    (keyword? segment)
    [segment (name segment)]

    (string? segment)
    [segment (keyword segment)]

    :else
    [segment]))

(defn- card-get-in
  [m path]
  (reduce
   (fn [current-levels segment]
     (->> current-levels
          (keep (fn [current]
                  (when (map? current)
                    current)))
          (mapcat (fn [current]
                    (keep (fn [candidate]
                            (when (contains? current candidate)
                              (get current candidate)))
                          (path-segment-candidates segment))))
          distinct
          vec))
   [m]
   (vec (or path []))))

(defn- card-id
  [card]
  (or (get card "original.db/id")
      (get card :original.db/id)
      (:db/id card)
      (:id card)))

(defn- card-index
  [cards card]
  (when-let [id (card-id card)]
    (first
     (keep-indexed
      (fn [index candidate]
        (when (= id (card-id candidate))
          index))
      (vec (or cards []))))))

(defn- selected-id-range
  [cards start-index end-index]
  (let [cards (vec (or cards []))
        start-idx (min start-index end-index)
        end-idx (max start-index end-index)]
    (->> (subvec cards start-idx (inc end-idx))
         (keep card-id)
         set)))

(defn- first-some
  [values]
  (first (filter some? values)))

(defn- card-values-at-path
  [card path]
  (when (seq path)
    (card-get-in card path)))

(defn modifier-click?
  "True when the click has the shift, the control or the command key. Such a click selects
   cards. A click with no key opens the preview of the card, when the listing has a preview."
  [e]
  (boolean (and e
                (or (dg/event-shift-key e)
                    (dg/event-ctrl-key e)
                    (dg/event-meta-key e)))))

(defn select-card!
  "Select the card that the user clicked. A click with the shift key adds the cards from the
   last selected card to this card. A click with the control or the command key adds this card,
   or removes it when it is selected. Another click selects this card only."
  [ui-state* card all-visible-items on-selection-changed e]
  (when-let [id (card-id card)]
    (let [shift? (and e
                      (dg/event-shift-key e))
          toggle? (and e
                       (not shift?)
                       (or (dg/event-ctrl-key e)
                           (dg/event-meta-key e)))
          clicked-index (card-index all-visible-items card)
          next-state (swap! ui-state*
                            (fn [state]
                              (let [selected-ids (or (:selected-ids state) #{})
                                    last-selected-index (:last-selected-index state)
                                    next-selected-ids (cond
                                                        (and shift?
                                                             (some? last-selected-index)
                                                             (some? clicked-index))
                                                        (into selected-ids
                                                              (selected-id-range all-visible-items
                                                                                 last-selected-index
                                                                                 clicked-index))

                                                        toggle?
                                                        (if (contains? selected-ids id)
                                                          (disj selected-ids id)
                                                          (conj selected-ids id))

                                                        :else
                                                        #{id})]
                                (assoc state
                                       :selected-ids next-selected-ids
                                       :last-selected-index clicked-index))))]
      (when (fn? on-selection-changed)
        (on-selection-changed (:selected-ids next-state))))))

(defn- flat-value
  [card field-info]
  (first-some
   (concat
    (card-values-at-path card (:flat-path field-info))
    (when-let [alias (:alias field-info)]
      [(get card (str "flat." alias))
       (get card (keyword (str "flat." alias)))]))))

(defn- field-value
  "The value of a field on a card. The index holds the start time of a field that
   shows the days since a time, see `common.opensearch.days-since`."
  [card field-info]
  (if-let [since-field (some->> (:alias field-info) (str "flat.") days-since/since-alias)]
    (let [since-alias (subs since-field (count "flat."))]
      (days-since/days (flat-value card {:flat-path ["flat" since-alias]
                                         :alias     since-alias})
                       (days-since/now-millis)))
    (flat-value card field-info)))

(defn- field-label
  [card field-info]
  (first-some
   (concat
    (card-values-at-path card (:label-path field-info))
    (when-let [alias (:alias field-info)]
      [(get card (str "labels." alias "_en"))
       (get card (keyword (str "labels." alias "_en")))]))))

(defn- field-currency
  "The currency of the value of a currency field on a card, as a keyword, or nil
   when the card has none. The card has it when its request asked for the
   currency field of the value. See `:currency-flat-path` of the field info."
  [card field-info]
  (when-let [flat-path (:currency-flat-path field-info)]
    (currency/stored->currency
     (first-some
      (concat
       (card-values-at-path card flat-path)
       (when-let [alias (second flat-path)]
         [(get card (str "flat." alias))
          (get card (keyword (str "flat." alias)))]))))))

(defn- format-field-value
  "The text of a value on a card. A date, a number, a currency value, a percentage
   and a multiplier have the text that the grid shows. Another value stays as it is.
   A currency value has the currency of its row, `currency`. When the card has no
   currency for it, the currency is the default currency of the tenant. A field
   with no multiplier of its own uses the default multiplier of the tenant."
  [{:keys [value field-info currency]}]
  (case (:data-type field-info)
    :data.type/datetime (when (some? value)
                          (or (fu/display-datetime-timestamp value)
                              (str value)))
    :data.type/date (when (some? value)
                      (or (fu/display-date-timestamp value)
                          (str value)))
    ;; Rich text is stored as HTML. A card shows plain text, so reduce the
    ;; markup to text. Without this branch the card shows the HTML literally,
    ;; for example <p>...</p>.
    :data.type/text-richtext (when (some? value)
                               (if (string? value)
                                 (ui-util/html->text value)
                                 (str value)))
    (or (value-format/format-value {:data-type         (:data-type field-info)
                                    :value             value
                                    :currency          currency
                                    :default-currency  @session/default-currency
                                    :number-multiplier (or (:number-multiplier field-info)
                                                           (:tenant/cur-multiplier @session/tenant))})
        value)))

(defn- field-display-value
  [card field-info]
  (or (field-label card field-info)
      (when-some [value (field-value card field-info)]
        (format-field-value {:value      value
                             :field-info field-info
                             :currency   (field-currency card field-info)}))))

(defn- display-value
  [value]
  (cond
    (nil? value) nil
    (string? value) value
    (keyword? value) (name value)
    (sequential? value) (string/join ", " (keep display-value value))
    :else (str value)))

(defn- non-blank
  "`s` when it is a non-blank string, else nil. A header field with an empty text,
   for example a task of a type other than simple, has a blank title, not nil."
  [s]
  (when-not (string/blank? s)
    s))

(defn title-value
  "The text of the title of a card: the first header field of `card-config`, or nil.
   `card-config` is a compiled card config."
  [card card-config]
  (when-let [title-fi (first (:header-fields card-config))]
    (display-value (field-display-value card title-fi))))

(defn title-fallback-value
  "The fallback text of the title of a card, when the header field has no value.
   It is the display value of the `:title-fallback` field of `card-config`, or nil.
   For example a task with no title shows its type, see
   `:result/title-fallback-attr`. `card-config` is a compiled card config."
  [card card-config]
  (when-let [fallback-fi (:title-fallback card-config)]
    (display-value (field-display-value card fallback-fi))))

(defn- footer-value
  [card footer-fi]
  {:field-info footer-fi
   :label      (or (:field-title footer-fi)
                   (display-value (field-label card footer-fi)))
   :value      (display-value (field-display-value card footer-fi))})

(defn body-row
  "The row of one body field of a card: a map with `:key`, `:label` and `:value`. `:value` is the
   text of the field, formatted as the card shows it: a date, a number, a currency value and a
   label have their text. It is nil when the card has no text for the field."
  [card field-info]
  (let [value (display-value (field-display-value card field-info))]
    {:key   (or (:alias field-info)
                (:field-path field-info))
     :label (or (:field-title field-info)
                (display-value (field-label card field-info)))
     :value (when-not (string/blank? value)
              value)}))

(defn body-rows
  "The rows of the body of a card: one `body-row` for each body field of the compiled
   `card-config` that has a text."
  [card card-config]
  (->> (:body-fields card-config)
       (map (partial body-row card))
       (filter :value)
       vec))

(defn date-field?
  "True when the field shows a date, or a date and a time."
  [field-info]
  (contains? #{:data.type/date :data.type/datetime}
             (:data-type field-info)))

(defn footer-props
  "Return the props of the footer of a card, or nil when the footer has no text.
   `footer` is the map that `footer-value` returns. The footer of a date has a
   calendar icon. The label of the field is the tooltip of the footer."
  [footer]
  (let [text (:value footer)]
    (when (and text
               (not (string/blank? text)))
      {:text  text
       :title (:label footer)
       :date? (date-field? (:field-info footer))})))

(defn- footer-items
  "Return the small fields of the footer of a card, one for each date field of
   `:footer-fields` that has a text. The footer of ZapKit does not truncate, so it
   holds short values only. The other footer fields are rows, see `footer-rows`."
  [card card-config]
  (->> (:footer-fields card-config)
       (filter date-field?)
       (keep (fn [footer-fi]
               (footer-props (footer-value card footer-fi))))
       vec))

(defn footer-rows
  "The rows of the footer fields that are not dates: one `body-row` for each field of
   `:footer-fields` that has a text, in the order of the config. The key of a row starts
   with `footer-`, so a field that is in the body and in the footer has two keys."
  [card card-config]
  (->> (:footer-fields card-config)
       (remove date-field?)
       (map (fn [footer-fi]
              (update (body-row card footer-fi) :key (partial str "footer-"))))
       (filter :value)
       vec))

(defn render-card
  "Render a card of a Kanban board. The card reads its title, its rows and its
   footer from the OpenSearch document `card`, as `card-config` says. `status` is
   the status of the card, see `ui.components.card.kanban/view`. `actions` is a
   vector of menu items. The menu of the card has the item Open before them.
   The rows are the body fields, three of them or two on a compact card, and then
   the footer fields that are not dates. The footer has the date fields. The title
   and the rows mark the parts that the search words `search-words` found."
  [{:keys [card card-config full-width? compact? width status actions search-words
           selected? collapsed? on-click on-double-click on-open on-toggle-collapse]}]
  (let [show-labels? (if (contains? card-config :show-labels?)
                       (boolean (:show-labels? card-config))
                       true)
        open! (fn [clicked-card]
                (cond
                  (fn? on-open) (on-open clicked-card)
                  (fn? on-double-click) (on-double-click clicked-card)
                  :else nil))
        open-item (when (or (fn? on-open)
                            (fn? on-double-click))
                    {:label    (loc/tr :open)
                     :on-click (fn [_]
                                 (open! card))})]
    [card-kanban/view
     {:title           (let [title (or (non-blank (title-value card card-config))
                                       (non-blank (title-fallback-value card card-config))
                                       "Untitled")]
                         (if (seq search-words)
                           [highlight-text/view {:text  title
                                                 :words search-words}]
                           title))
      :status          status
      :rows            (cond->> (into (vec (take (if compact? 2 3)
                                                 (body-rows card card-config)))
                                      (footer-rows card card-config))
                         (seq search-words)
                         (mapv (fn [row]
                                 (update row :value (fn [value]
                                                      [highlight-text/view {:text  value
                                                                            :words search-words}])))))
      :show-labels?    show-labels?
      :footer          (footer-items card card-config)
      :menu-items      (vec (concat (when open-item
                                      [open-item])
                                    actions))
      :selected?       selected?
      :collapsed?      collapsed?
      :full-width?     full-width?
      :width           width
      :card-id         (card-id card)
      :on-click        (fn [e]
                         (when (fn? on-click)
                           (on-click e card)))
      :on-double-click (fn [e]
                         (dg/stop-propagation e)
                         (when (fn? on-double-click)
                           (on-double-click card)))
      :on-title-click  (fn [e]
                         (dg/prevent-and-stop e)
                         (when (fn? on-click)
                           (on-click e card))
                         (when (fn? on-toggle-collapse)
                           (on-toggle-collapse card)))}]))
